// ModeratorPanel.tsx
import React, { useCallback, useMemo, useState } from "react";
import {
  AppBar,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Drawer,
  Fab,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ForumIcon from "@mui/icons-material/Forum";
import PollIcon from "@mui/icons-material/Poll";
import CasinoIcon from "@mui/icons-material/Casino";
import PeopleIcon from "@mui/icons-material/People";
import YouTubeIcon from "@mui/icons-material/YouTube";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import { useSearchParams } from "react-router-dom";

// Feature modules (unchanged)
import LiveChat from "../LiveChat";
import LivePolls from "../LivePolls";
import BingoManagement from "../Talent/BingoManagement";
import GuestManagement from "../Talent/GuestManagement";
import YoutubeRequest from "../Talent/YoutubeRequest";

/** ---------------- Netflix-like tokens ---------------- */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  panelAlt: "#1c1c1c",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
};
const RADIUS = 2; // 16px

/** ---------------- Shared surface wrapper ---------------- */
function Surface({
  children,
  sx,
  as = Paper,
}: {
  children: React.ReactNode;
  sx?: any;
  as?: any;
}) {
  const Comp = as;
  return (
    <Comp
      elevation={0}
      sx={{
        p: 2,
        borderRadius: RADIUS,
        bgcolor: COLORS.panel,
        border: "1px solid",
        borderColor: COLORS.border,
        color: COLORS.text,
        ...sx,
      }}
    >
      {children}
    </Comp>
  );
}

/** ---------------- StageLink red button ---------------- */
function RedButton(props: React.ComponentProps<typeof Button>) {
  return (
    <Button
      {...props}
      variant="contained"
      sx={{
        borderRadius: RADIUS,
        fontWeight: 800,
        textTransform: "none",
        bgcolor: COLORS.red,
        "&:hover": { bgcolor: COLORS.redHover },
        ...props.sx,
      }}
    />
  );
}

/** ---------------- Reusable Accordion Panel ---------------- */
function Panel({
  title,
  icon,
  defaultExpanded = false,
  onRemove,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  onRemove?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        bgcolor: COLORS.panel,
        color: COLORS.text,
        border: "1px solid",
        borderColor: COLORS.border,
        borderRadius: RADIUS,
        "&::before": { display: "none" },
        overflow: "hidden",
        "&.Mui-expanded": { borderColor: COLORS.red },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon htmlColor={COLORS.textMuted} />}
        sx={{
          minHeight: 56,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            gap: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          {icon}
          <Typography sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
            {title}
          </Typography>
        </Box>
        {onRemove && (
          <Tooltip title="Remove from layout">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              sx={{
                color: COLORS.textMuted,
                "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </AccordionSummary>
      <Divider sx={{ borderColor: COLORS.border }} />
      <AccordionDetails sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>{children}</Box>
      </AccordionDetails>
    </Accordion>
  );
}

/** ---------------- Tool registry ---------------- */
type ToolId = "chat" | "guests" | "polls" | "bingo" | "youtube";

const TOOL_META: Record<ToolId, { label: string; icon: React.ReactNode }> = {
  chat: { label: "Live Chat", icon: <ForumIcon fontSize="small" /> },
  guests: { label: "Guest Management", icon: <PeopleIcon fontSize="small" /> },
  polls: { label: "Polls", icon: <PollIcon fontSize="small" /> },
  bingo: { label: "Bingo Break", icon: <CasinoIcon fontSize="small" /> },
  youtube: { label: "YouTube Requests", icon: <YouTubeIcon fontSize="small" /> },
};

/** ---------------- Read-only Stream Info Bar (no video controls) ---------------- */
function ReadOnlyStreamBar({ bookingId }: { bookingId: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        bgcolor: COLORS.panelAlt,
        borderTop: "1px solid",
        borderColor: COLORS.border,
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 0.25,
          borderRadius: 999,
          bgcolor: "rgba(229,9,20,0.12)",
          border: "1px solid",
          borderColor: COLORS.red,
          lineHeight: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 900, letterSpacing: "0.08em", color: COLORS.red }}
        >
          LIVE
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
        Stream status is <b>view-only</b> for moderators. Talent manages start/stop,
        mic/cam, and scenes.{" "}
        <Typography component="span" variant="body2" sx={{ color: COLORS.textMuted }}>
          (Booking: {bookingId || "—"})
        </Typography>
      </Typography>
    </Box>
  );
}

/** ---------------- Moderator Panel ---------------- */
export default function ModeratorPanel({
  children,
}: {
  /** Video player (from talent’s stream) is passed in as children */
  children: React.ReactNode;
}) {
  const [params] = useSearchParams();
  const bookingId = params.get("bookingId") ?? "";

  const lgUp = useMediaQuery("(min-width:1200px)");
  const smDown = useMediaQuery("(max-width:899px)");

  const [toolsOpen, setToolsOpen] = useState(false);

  // Active tools shown in the main layout. Seed with chat & guests for desktop UX.
  const [activeTools, setActiveTools] = useState<ToolId[]>(["chat", "guests"]);

  const isActive = useCallback(
    (id: ToolId) => activeTools.includes(id),
    [activeTools]
  );

  const addTool = useCallback(
    (id: ToolId) => {
      setActiveTools((prev) => (prev.includes(id) ? prev : [...prev, id]));
      if (smDown) setToolsOpen(false); // mobile: close tools after choosing
    },
    [smDown]
  );

  const removeTool = useCallback((id: ToolId) => {
    setActiveTools((prev) => prev.filter((t) => t !== id));
  }, []);

  const toggleTool = useCallback(
    (id: ToolId) => {
      if (isActive(id)) removeTool(id);
      else addTool(id);
    },
    [isActive, addTool, removeTool]
  );

  /** Render a tool by id */
  const renderTool = useCallback(
    (id: ToolId) => {
      switch (id) {
        case "chat":
          return (
            <Panel
              title={TOOL_META.chat.label}
              icon={TOOL_META.chat.icon}
              defaultExpanded
              onRemove={() => removeTool("chat")}
            >
              <LiveChat
                bookingId={bookingId}
                username="moderator"
                height={smDown ? "45vh" : "50vh"}
              />
            </Panel>
          );
        case "guests":
          return (
            <Panel
              title={TOOL_META.guests.label}
              icon={TOOL_META.guests.icon}
              onRemove={() => removeTool("guests")}
            >
              <GuestManagement bookingId={bookingId} />
            </Panel>
          );
        case "polls":
          return (
            <Panel
              title={TOOL_META.polls.label}
              icon={TOOL_META.polls.icon}
              onRemove={() => removeTool("polls")}
            >
              <LivePolls bookingId={bookingId} />
            </Panel>
          );
        case "bingo":
          return (
            <Panel
              title={TOOL_META.bingo.label}
              icon={TOOL_META.bingo.icon}
              onRemove={() => removeTool("bingo")}
            >
              <BingoManagement bookingId={bookingId} />
            </Panel>
          );
        case "youtube":
          return (
            <Panel
              title={TOOL_META.youtube.label}
              icon={TOOL_META.youtube.icon}
              onRemove={() => removeTool("youtube")}
            >
              <YoutubeRequest bookingId={bookingId} />
            </Panel>
          );
        default:
          return null;
      }
    },
    [bookingId, removeTool, smDown]
  );

  /** Panels to render on the right (desktop) or below player (mobile/tablet) */
  const renderedPanels = useMemo(
    () => activeTools.map((id) => <Box key={id}>{renderTool(id)}</Box>),
    [activeTools, renderTool]
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.bg, color: COLORS.text }}>
      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          borderBottom: "1px solid",
          borderColor: COLORS.border,
          backdropFilter: "blur(8px)",
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.9), rgba(10,10,10,0.65))",
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: COLORS.panelAlt,
              border: "1px solid",
              borderColor: COLORS.border,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: "-0.01em" }}>
            StageLink • Moderator
          </Typography>

          <Box
            sx={{
              ml: 1,
              px: 1,
              py: 0.25,
              borderRadius: 999,
              bgcolor: "rgba(229,9,20,0.12)",
              border: "1px solid",
              borderColor: COLORS.red,
              lineHeight: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 900, letterSpacing: "0.08em", color: COLORS.red }}
            >
              LIVE
            </Typography>
          </Box>

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <RedButton onClick={() => setToolsOpen(true)} startIcon={<BuildRoundedIcon />}>
              Tools
            </RedButton>
            <IconButton size="small" aria-label="more" sx={{ color: COLORS.textMuted }}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2}>
          {/* LEFT: Player (view-only) */}
          <Grid item xs={12} lg={8}>
            <Surface sx={{ p: 0, overflow: "hidden" }}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "16 / 9",
                  bgcolor: "#000",
                }}
              >
                {/* Talent-owned stream content */}
                <Box sx={{ position: "absolute", inset: 0 }}>{children}</Box>
              </Box>

              <Divider sx={{ borderColor: COLORS.border }} />

              {/* Read-only status bar (no controls) */}
              <ReadOnlyStreamBar bookingId={bookingId} />
            </Surface>

            {/* On ≤lg, show the chosen tools below the player */}
            <Box sx={{ mt: 2, display: "grid", gap: 2 }}>{!lgUp && renderedPanels}</Box>
          </Grid>

          {/* RIGHT: tools column (only on large screens) */}
          {lgUp && (
            <Grid item xs={12} lg={4}>
              <Box sx={{ display: "grid", gap: 2 }}>{renderedPanels}</Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* TOOLS DRAWER */}
      <Drawer
        anchor="right"
        open={toolsOpen}
        onClose={() => setToolsOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: smDown ? "85vw" : 460,
            bgcolor: COLORS.bg,
            color: COLORS.text,
            borderLeft: "1px solid",
            borderColor: COLORS.border,
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: COLORS.border,
            background:
              "linear-gradient(180deg, rgba(20,20,20,0.95), rgba(20,20,20,0.8))",
            backdropFilter: "blur(8px)",
          }}
        >
          <BuildRoundedIcon htmlColor={COLORS.text} />
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Moderator Tools
          </Typography>
          <Box sx={{ ml: "auto" }} />
          <IconButton
            onClick={() => setToolsOpen(false)}
            aria-label="close tools"
            sx={{ color: COLORS.textMuted }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {/* Tool toggles */}
        <Box sx={{ p: 2 }}>
          <Surface sx={{ p: 0 }}>
            <List dense sx={{ py: 0 }}>
              {(
                [
                  { id: "chat", meta: TOOL_META.chat },
                  { id: "guests", meta: TOOL_META.guests },
                  { id: "polls", meta: TOOL_META.polls },
                  { id: "bingo", meta: TOOL_META.bingo },
                  { id: "youtube", meta: TOOL_META.youtube },
                ] as { id: ToolId; meta: { label: string; icon: React.ReactNode } }[]
              ).map(({ id, meta }) => {
                const active = isActive(id);
                return (
                  <ListItem
                    key={id}
                    secondaryAction={
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          onClick={() => toggleTool(id)}
                          startIcon={
                            active ? (
                              <RemoveCircleOutlineRoundedIcon fontSize="small" />
                            ) : (
                              <AddCircleOutlineRoundedIcon fontSize="small" />
                            )
                          }
                          sx={{
                            borderRadius: RADIUS,
                            textTransform: "none",
                            fontWeight: 700,
                            px: 1.25,
                            color: active ? COLORS.text : "#fff",
                            bgcolor: active ? "rgba(255,255,255,0.08)" : COLORS.red,
                            "&:hover": {
                              bgcolor: active ? "rgba(255,255,255,0.12)" : COLORS.redHover,
                            },
                            minWidth: 0,
                          }}
                        >
                          {active ? "Remove" : "Show"}
                        </Button>
                      </Stack>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: COLORS.textMuted }}>
                      {meta.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 700 }}>{meta.label}</Typography>}
                      secondary={
                        <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                          {active ? "Visible in layout" : "Hidden"}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Surface>
        </Box>

        {/* Tip */}
        <Box sx={{ p: 2, pt: 0, color: COLORS.textMuted }}>
          <Typography variant="caption">
            Tip: Moderators can manage chat, guests, polls, bingo, and media requests. Video
            streaming is talent-controlled.
          </Typography>
        </Box>
      </Drawer>

      {/* Mobile FAB: open Tools */}
      {smDown && (
        <Fab
          onClick={() => setToolsOpen(true)}
          aria-label="open tools"
          sx={{
            position: "fixed",
            right: 16,
            bottom: 16,
            borderRadius: RADIUS,
            fontWeight: 800,
            color: COLORS.text,
            bgcolor: COLORS.red,
            "&:hover": { bgcolor: COLORS.redHover },
          }}
        >
          <BuildRoundedIcon />
        </Fab>
      )}
    </Box>
  );
}
